import api from './api';
import publicApi from './publicApi';
import type {
    ApiResponse,
    ArticleCategoryPublicResponse,
    ArticleCategoryRequest,
    ArticleCategoryResponse,
    ArticleCommentResponse,
    ArticleHistoryResponse,
    ArticleImageRequest,
    ArticleImageResponse,
    ArticleListResponse,
    ArticleStatisticsResponse,
    ArticleTagPublicResponse,
    ArticleTagRequest,
    ArticleTagResponse,
    ArticleType,
    ArticleWishlistItemResponse,
    CreateCommentRequest,
    EventArticleAdminResponse,
    EventArticleDetailResponse,
    EventArticleUpsertRequest,
    ArticleAnalytics,
    ArticleMetrics,
    DashboardAnalytics,
    ReactionSummary,
    ReactionType,
    SpringPage,
} from '../types/article';

const toApiResponse = <T>(payload: any): ApiResponse<T> => {
    if (payload && typeof payload.status === 'boolean' && ('body' in payload || 'message' in payload)) {
        return {
            status: Boolean(payload.status),
            message: payload?.message || '',
            body: (payload?.body ?? null) as T | null,
        };
    }

    return {
        status: true,
        message: '',
        body: (payload ?? null) as T | null,
    };
};

export const articleAPI = {
    getArticleBySlug: async (slug: string): Promise<ApiResponse<EventArticleDetailResponse>> => {
        const response = await publicApi.get(`/api/articles/${encodeURIComponent(slug)}`);
        return toApiResponse<EventArticleDetailResponse>(response.data);
    },

    getArticleById: async (articleId: number): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.get(`/api/admin/articles/${articleId}`);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    getArticleByActivityId: async (activityId: number): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.get(`/api/admin/articles/by-activity/${activityId}`);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    createArticle: async (data: EventArticleUpsertRequest): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.post('/api/admin/articles', data);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    updateArticle: async (articleId: number, data: EventArticleUpsertRequest): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.put(`/api/admin/articles/${articleId}`, data);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    publishArticle: async (articleId: number): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.put(`/api/admin/articles/${articleId}/publish`);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    unpublishArticle: async (articleId: number): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.put(`/api/admin/articles/${articleId}/unpublish`);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    addToWishlist: async (slug: string): Promise<ApiResponse<null>> => {
        const response = await api.post(`/api/articles/${encodeURIComponent(slug)}/wishlist`);
        return toApiResponse<null>(response.data);
    },

    removeFromWishlist: async (slug: string): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/articles/${encodeURIComponent(slug)}/wishlist`);
        return toApiResponse<null>(response.data);
    },

    getWishlistedArticles: async (params?: {
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleWishlistItemResponse>>> => {
        const response = await api.get('/api/articles/wishlist', { params });
        return toApiResponse<SpringPage<ArticleWishlistItemResponse>>(response.data);
    },

    // Analytics endpoints
    getArticleAnalytics: async (articleId: number): Promise<ApiResponse<ArticleAnalytics>> => {
        const response = await api.get(`/api/admin/articles/${articleId}/analytics`);
        return toApiResponse<ArticleAnalytics>(response.data);
    },

    getArticlesAnalytics: async (filters?: { startDate?: string; endDate?: string; status?: 'all' | 'published' }): Promise<ApiResponse<ArticleMetrics[]>> => {
        const response = await api.get('/api/admin/articles/analytics', { params: filters });
        return toApiResponse<ArticleMetrics[]>(response.data);
    },

    getDashboardAnalytics: async (): Promise<ApiResponse<DashboardAnalytics>> => {
        const response = await api.get('/api/admin/articles/analytics/dashboard');
        return toApiResponse<DashboardAnalytics>(response.data);
    },

    /**
     * @deprecated Detail page already increments views via GET /articles/{slug}.
     * Do not call this together with getArticleBySlug (causes double/triple counting).
     * Keep only for rare preview paths that never hit GET detail.
     */
    trackArticleView: async (slug: string): Promise<void> => {
        try {
            await publicApi.post(`/api/articles/${encodeURIComponent(slug)}/track-view`);
        } catch {
            // Silently fail - view tracking should not block article loading
        }
    },

    joinWaitlist: async (slug: string): Promise<ApiResponse<null>> => {
        const response = await api.post(`/api/articles/${encodeURIComponent(slug)}/waitlist`);
        return toApiResponse<null>(response.data);
    },

    // Get calendar event
    getArticleCalendar: async (slug: string): Promise<Blob> => {
        const response = await publicApi.get(`/api/articles/${encodeURIComponent(slug)}/calendar`, {
            responseType: 'blob',
        });
        return response.data;
    },

    // List endpoints
    getPublicArticlesList: async (params?: {
        status?: 'published' | 'all';
        featured?: boolean;
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleListResponse>>> => {
        const response = await publicApi.get('/api/articles', { params });
        return toApiResponse<SpringPage<ArticleListResponse>>(response.data);
    },

    getArticlesList: async (params?: {
        status?: 'all' | 'published' | 'draft';
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleListResponse>>> => {
        const response = await api.get('/api/admin/articles', { params });
        return toApiResponse<SpringPage<ArticleListResponse>>(response.data);
    },

    // Featured articles
    getFeaturedArticles: async (limit?: number): Promise<ApiResponse<ArticleListResponse[]>> => {
        const response = await publicApi.get('/api/articles/featured', { params: { limit } });
        return toApiResponse<ArticleListResponse[]>(response.data);
    },

    // Articles by category
    getArticlesByCategory: async (categorySlug: string, params?: {
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleListResponse>>> => {
        const response = await publicApi.get(`/api/articles/category/${encodeURIComponent(categorySlug)}`, { params });
        return toApiResponse<SpringPage<ArticleListResponse>>(response.data);
    },

    getArticlesByTag: async (tagSlug: string, params?: {
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleListResponse>>> => {
        const response = await publicApi.get(`/api/articles/tag/${encodeURIComponent(tagSlug)}`, { params });
        return toApiResponse<SpringPage<ArticleListResponse>>(response.data);
    },

    getPublicTags: async (): Promise<ApiResponse<ArticleTagPublicResponse[]>> => {
        const response = await publicApi.get('/api/articles/tags');
        return toApiResponse<ArticleTagPublicResponse[]>(response.data);
    },

    // Search articles
    searchArticles: async (keyword: string, params?: {
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleListResponse>>> => {
        const response = await publicApi.get('/api/articles/search', {
            params: { keyword, q: keyword, ...params },
        });
        return toApiResponse<SpringPage<ArticleListResponse>>(response.data);
    },

    getRelatedArticles: async (slug: string, params?: { limit?: number }): Promise<ApiResponse<ArticleListResponse[]>> => {
        const response = await publicApi.get(`/api/articles/${encodeURIComponent(slug)}/related`, { params });
        return toApiResponse<ArticleListResponse[]>(response.data);
    },

    // Admin statistics
    getStatistics: async (): Promise<ApiResponse<ArticleStatisticsResponse>> => {
        const response = await api.get('/api/admin/articles/statistics');
        return toApiResponse<ArticleStatisticsResponse>(response.data);
    },

    // Categories management
    getCategories: async (): Promise<ApiResponse<ArticleCategoryPublicResponse[]>> => {
        const response = await publicApi.get('/api/articles/categories');
        return toApiResponse<ArticleCategoryPublicResponse[]>(response.data);
    },

    getAdminCategories: async (): Promise<ApiResponse<ArticleCategoryResponse[]>> => {
        const response = await api.get('/api/admin/articles/categories');
        return toApiResponse<ArticleCategoryResponse[]>(response.data);
    },

    createCategory: async (data: ArticleCategoryRequest): Promise<ApiResponse<ArticleCategoryResponse>> => {
        const response = await api.post('/api/admin/articles/categories', data);
        return toApiResponse<ArticleCategoryResponse>(response.data);
    },

    updateCategory: async (categoryId: number, data: ArticleCategoryRequest): Promise<ApiResponse<ArticleCategoryResponse>> => {
        const response = await api.put(`/api/admin/articles/categories/${categoryId}`, data);
        return toApiResponse<ArticleCategoryResponse>(response.data);
    },

    deleteCategory: async (categoryId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/admin/articles/categories/${categoryId}`);
        return toApiResponse<null>(response.data);
    },

    getAdminTags: async (): Promise<ApiResponse<ArticleTagResponse[]>> => {
        const response = await api.get('/api/admin/articles/tags');
        return toApiResponse<ArticleTagResponse[]>(response.data);
    },

    createTag: async (data: ArticleTagRequest): Promise<ApiResponse<ArticleTagResponse>> => {
        const response = await api.post('/api/admin/articles/tags', data);
        return toApiResponse<ArticleTagResponse>(response.data);
    },

    updateTag: async (tagId: number, data: ArticleTagRequest): Promise<ApiResponse<ArticleTagResponse>> => {
        const response = await api.put(`/api/admin/articles/tags/${tagId}`, data);
        return toApiResponse<ArticleTagResponse>(response.data);
    },

    deleteTag: async (tagId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/admin/articles/tags/${tagId}`);
        return toApiResponse<null>(response.data);
    },

    // Image gallery management
    uploadArticleImages: async (articleId: number, files: FormData): Promise<ApiResponse<any>> => {
        const response = await api.post(`/api/admin/articles/${articleId}/images`, files, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return toApiResponse(response.data);
    },

    addArticleImage: async (articleId: number, data: ArticleImageRequest): Promise<ApiResponse<ArticleImageResponse>> => {
        const response = await api.post(`/api/admin/articles/${articleId}/images`, data);
        return toApiResponse<ArticleImageResponse>(response.data);
    },

    removeArticleImage: async (articleId: number, imageId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/admin/articles/${articleId}/images/${imageId}`);
        return toApiResponse<null>(response.data);
    },

    // =============================================
    // Phase 1/2/3 — New API Functions
    // =============================================

    // ===== COMMENTS (Student) =====

    createComment: async (slug: string, data: CreateCommentRequest): Promise<ApiResponse<ArticleCommentResponse>> => {
        const response = await api.post(`/api/articles/${encodeURIComponent(slug)}/comments`, data);
        return toApiResponse<ArticleCommentResponse>(response.data);
    },

    getComments: async (slug: string, params?: { page?: number; size?: number }): Promise<ApiResponse<SpringPage<ArticleCommentResponse>>> => {
        const response = await publicApi.get(`/api/articles/${encodeURIComponent(slug)}/comments`, { params });
        return toApiResponse<SpringPage<ArticleCommentResponse>>(response.data);
    },

    deleteOwnComment: async (commentId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/articles/comments/${commentId}`);
        return toApiResponse<null>(response.data);
    },

    // ===== REACTIONS =====

    addReaction: async (slug: string, type: ReactionType): Promise<ApiResponse<null>> => {
        const response = await api.post(`/api/articles/${encodeURIComponent(slug)}/reaction`, null, {
            params: { type },
        });
        return toApiResponse<null>(response.data);
    },

    removeReaction: async (slug: string): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/articles/${encodeURIComponent(slug)}/reaction`);
        return toApiResponse<null>(response.data);
    },

    getReactionSummary: async (slug: string): Promise<ApiResponse<ReactionSummary>> => {
        const response = await publicApi.get(`/api/articles/${encodeURIComponent(slug)}/reactions`);
        return toApiResponse<ReactionSummary>(response.data);
    },

    // ===== SHARE TRACKING =====

    trackShare: async (slug: string): Promise<void> => {
        try {
            await publicApi.post(`/api/articles/${encodeURIComponent(slug)}/track-share`);
        } catch {
            // Silent fail — same pattern as trackArticleView
        }
    },

    // ===== READING HISTORY =====

    getReadingHistory: async (params?: { page?: number; size?: number }): Promise<ApiResponse<SpringPage<ArticleHistoryResponse>>> => {
        const response = await api.get('/api/articles/history', { params });
        return toApiResponse<SpringPage<ArticleHistoryResponse>>(response.data);
    },

    deleteHistoryItem: async (historyId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/articles/history/${historyId}`);
        return toApiResponse<null>(response.data);
    },

    clearAllHistory: async (): Promise<ApiResponse<null>> => {
        const response = await api.delete('/api/articles/history');
        return toApiResponse<null>(response.data);
    },

    // ===== TRENDING =====

    getTrendingArticles: async (params?: { days?: number; limit?: number }): Promise<ApiResponse<ArticleListResponse[]>> => {
        const response = await publicApi.get('/api/articles/trending', { params });
        return toApiResponse<ArticleListResponse[]>(response.data);
    },

    // ===== ADMIN: SET PRIMARY =====

    setPrimaryArticle: async (articleId: number): Promise<ApiResponse<EventArticleAdminResponse>> => {
        const response = await api.put(`/api/admin/articles/${articleId}/set-primary`);
        return toApiResponse<EventArticleAdminResponse>(response.data);
    },

    // ===== ADMIN: ADVANCED FILTER =====

    getAdminArticlesFiltered: async (params?: {
        status?: 'PUBLISHED' | 'DRAFT' | 'ALL';
        activityId?: number;
        categoryId?: number;
        articleType?: ArticleType;
        featured?: boolean;
        pinned?: boolean;
        primary?: boolean;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<EventArticleAdminResponse>>> => {
        const response = await api.get('/api/admin/articles', { params });
        return toApiResponse<SpringPage<EventArticleAdminResponse>>(response.data);
    },

    // ===== ADMIN: EXPORT EXCEL =====

    exportArticlesExcel: async (params?: Record<string, any>): Promise<Blob> => {
        const response = await api.get('/api/admin/articles/export', {
            params,
            responseType: 'blob',
        });
        return response.data;
    },

    // ===== ADMIN: COMMENT MODERATION =====

    getAdminComments: async (articleId: number, params?: {
        page?: number;
        size?: number;
    }): Promise<ApiResponse<SpringPage<ArticleCommentResponse>>> => {
        const response = await api.get(`/api/admin/articles/${articleId}/comments`, { params });
        return toApiResponse<SpringPage<ArticleCommentResponse>>(response.data);
    },

    hideComment: async (commentId: number): Promise<ApiResponse<ArticleCommentResponse>> => {
        const response = await api.put(`/api/admin/articles/comments/${commentId}/hide`);
        return toApiResponse<ArticleCommentResponse>(response.data);
    },

    unhideComment: async (commentId: number): Promise<ApiResponse<ArticleCommentResponse>> => {
        const response = await api.put(`/api/admin/articles/comments/${commentId}/unhide`);
        return toApiResponse<ArticleCommentResponse>(response.data);
    },

    adminDeleteComment: async (commentId: number): Promise<ApiResponse<null>> => {
        const response = await api.delete(`/api/admin/articles/comments/${commentId}`);
        return toApiResponse<null>(response.data);
    },
};
